import React, { useState, useEffect } from "react";
import { FaGithub, FaExternalLinkAlt } from "react-icons/fa";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const StarBackground = () => {
  const [stars, setStars] = useState([]);
  const [meteors, setMeteors] = useState([]);
  const [parallaxX, setParallaxX] = useState(0);
  const [parallaxY, setParallaxY] = useState(0);

  useEffect(() => {
    const generateStars = () => {
      const numberOfStars = Math.floor((window.innerWidth * window.innerHeight) / 8000);
      const newStars = Array.from({ length: numberOfStars }, (_, i) => ({
        id: i,
        size: Math.random() * 2 + 1,
        x: Math.random() * 100,
        y: Math.random() * 100,
        opacity: Math.random() * 0.6 + 0.3,
        speed: Math.random() * 2 + 1,
      }));
      setStars(newStars);
    };

    const generateMeteors = () => {
      const numberOfMeteors = 4;
      const newMeteors = Array.from({ length: numberOfMeteors }, (_, i) => ({
        id: i,
        size: Math.random() * 1.5 + 1,
        x: Math.random() * 100,
        y: Math.random() * 20,
        delay: Math.random() * 10,
        animationDuration: Math.random() * 3 + 3,
      }));
      setMeteors(newMeteors);
    };

    generateStars();
    generateMeteors();

    const handleResize = () => generateStars();
    const handleMouseMove = (e) => {
      setParallaxX((e.clientX / window.innerWidth - 0.5) * 10);
      setParallaxY((e.clientY / window.innerHeight - 0.5) * 10);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {stars.map((star) => (
        <div
          key={star.id}
          style={{
            width: `${star.size}px`,
            height: `${star.size}px`,
            left: `${star.x}%`,
            top: `${star.y}%`,
            opacity: star.opacity,
            position: "absolute",
            borderRadius: "9999px",
            backgroundColor: "white",
            boxShadow: "0 0 15px 3px rgba(255, 255, 255, 0.5)",
            animation: `twinkle ${star.speed}s ease-in-out infinite`,
            transform: `translate(${parallaxX * (star.speed / 2)}px, ${parallaxY * (star.speed / 2)}px)`,
          }}
        />
      ))}
      {meteors.map((meteor) => (
        <div
          key={meteor.id}
          style={{
            width: `${meteor.size * 50}px`,
            height: `${meteor.size * 2}px`,
            left: `${meteor.x}%`,
            top: `${meteor.y}%`,
            animationDelay: `${meteor.delay}s`,
            animationDuration: `${meteor.animationDuration}s`,
            position: "absolute",
            borderRadius: "9999px",
            background: "linear-gradient(to right, white, transparent)",
            boxShadow: "0 0 20px 5px rgba(255, 255, 255, 0.3)",
            transform: "rotate(-45deg)",
            animationName: "meteor",
            animationTimingFunction: "linear",
            animationIterationCount: "infinite",
          }}
        />
      ))}
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.3); }
        }
        @keyframes meteor {
          0% { transform: translate(0, 0) rotate(-45deg); opacity: 1; }
          100% { transform: translate(300px, 300px) rotate(-45deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

const projectsData = [
  {
    title: "Quiz App",
    description: "A feature-rich React-based quiz app with lifelines, a timer, and leaderboard.",
    tech: ["React", "Node.js", "MongoDB", "Express", "Tailwind CSS"],
    live: "https://quiz-app-by-harsh.netlify.app/",
    github: "https://github.com/HarshPariya/Quiz-App-Frontend",
    category: "fullstack",
  },
  {
    title: "Todo List App",
    description: "A responsive to-do list app with full CRUD and backend integration.",
    tech: ["React", "Express", "MongoDB", "Tailwind CSS"],
    live: "https://todo-list-by-harshp.netlify.app/",
    github: "https://github.com/HarshPariya/Todo-list-Frontend",
    category: "frontend",
  },
  {
    title: "Student Management System",
    description: "A responsive portal with student data and weather integration.",
    tech: ["React", "CSS"],
    live: "https://student-managment-system-by-harshp.netlify.app/",
    github: "https://github.com/HarshPariya/Student-Management-System",
    category: "frontend",
  },
  {
    title: "Portfolio Website",
    description: "Animated personal portfolio using React, Tailwind, and Framer Motion.",
    tech: ["React", "Tailwind CSS", "Framer Motion"],
    live: "#",
    github: "https://github.com/HarshPariya/Portfolio",
    category: "frontend",
  },
];

const Projects = () => {
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const filteredProjects =
    filter === "all" ? projectsData : projectsData.filter((project) => project.category === filter);

  const handleNavigateToContact = () => {
    navigate("/Contact");
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  return (
    <section className="relative text-white px-6 py-24 min-h-screen overflow-hidden">
      <StarBackground />
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-gray-800 opacity-80 z-0" />

      <div className="relative z-10 max-w-6xl mx-auto text-center">
        <h2 className="text-4xl font-extrabold mb-4 bg-gradient-to-r from-emerald-400 via-sky-400 to-indigo-500 text-transparent bg-clip-text">
          My Projects
        </h2>
        <p className="text-lg text-slate-300 mb-12 max-w-2xl mx-auto">
          A collection of full-stack, frontend, and personal works built with modern technologies.
        </p>

        <div className="flex justify-center gap-4 mb-12 flex-wrap">
          {["all", "fullstack", "frontend"].map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                filter === type
                  ? "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white shadow-md"
                  : "border border-slate-500 text-slate-300 hover:bg-slate-800"
              }`}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 justify-items-center">
          {filteredProjects.map((project, index) => (
            <motion.div
              key={index}
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="w-full max-w-md bg-white/5 backdrop-blur-md rounded-xl p-6 border border-indigo-500/30 shadow-lg hover:shadow-indigo-500/40 transition-shadow"
            >
              <h3 className="text-xl font-semibold text-purple-300 mb-2">{project.title}</h3>
              <p className="text-sm text-slate-300 mb-4">{project.description}</p>

              <div className="flex flex-wrap gap-2 mb-4 justify-center">
                {project.tech.map((tech, i) => (
                  <span
                    key={i}
                    className="bg-purple-700/80 text-white text-xs px-3 py-1 rounded-full"
                  >
                    {tech}
                  </span>
                ))}
              </div>

              <div className="flex justify-center gap-6 mt-4 text-sm">
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-slate-300 hover:text-purple-300 transition"
                >
                  <FaGithub className="text-lg" /> GitHub
                </a>
                {project.live && project.live !== "#" && (
                  <a
                    href={project.live}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-300 hover:text-purple-300 transition"
                  >
                    <FaExternalLinkAlt className="text-sm" /> Live Demo
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mt-20 bg-gradient-to-r from-indigo-700 via-purple-800 to-indigo-900 rounded-2xl p-10 shadow-2xl"
        >
          <h3 className="text-2xl sm:text-3xl font-bold mb-3">Want to See More?</h3>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">
            Explore more on my GitHub or reach out to collaborate on exciting projects or freelance work.
          </p>
          <button
            onClick={handleNavigateToContact}
            className="inline-block bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-3 rounded-lg text-white font-medium hover:scale-105 transition-transform"
          >
            Let’s Connect
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
